import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getCmsApiBaseUrl } from 'src/services/api.config';

interface Filter {
  label: string;
  value: string;
}

export interface sessionStatsData {
  Fail: number;
  NeedsReview: number;
  Pass: number;
  Pending: number;
  Total: number;
}

interface MonthlyStats {
  Date: {
    Year: number;
    Month: number;
  };
  TotalCount: number;
  TotalValidationsPass: number;
  TotalValidationsNeedsReview: number;
  TotalValidationsFail: number;
}

interface StatisticState {
  sliceStatistics: Array<any>;
  selectedDateStats: sessionStatsData;
  yearlyStatistics: MonthlyStats[];
  isLoading: boolean;
  error: string | null;
  filters: Filter[];
  liveData: boolean;
}

const initialState: StatisticState = {
  sliceStatistics: [],
  selectedDateStats: { Total: 0, Pass: 0, Fail: 0, NeedsReview: 0, Pending: 0 },
  yearlyStatistics: [],
  isLoading: false,
  error: null,
  filters: [],
  liveData: false,
};

export const fetchStatistics = createAsyncThunk(
  'statistics/fetchStatistics',
  async ({
    __raw,
    period,
    authOrgId,
    liveData,
    startDate,
    endDate,
    today,
  }: {
    __raw: string;
    period: string;
    authOrgId: string;
    liveData: boolean;
    startDate?: string;
    endDate?: string;
    today?: boolean;
  }) => {
    try {
      const baseUrl = liveData
        ? `${getCmsApiBaseUrl(true)}/sessions/stats`
        : 'https://dev.id.sonio-group.com/api/console/v1/sessions/stats';

      // Build query parameters
      const params = new URLSearchParams();
      params.append('authOrgId', authOrgId);

      if (today) {
        params.append('today', 'true');
      } else if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      const response = await axios.get(`${baseUrl}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${__raw}`,
        },
      });

      return { data: response.data, period };
    } catch (error) {
      throw error;
    }
  },
);

export const fetchYearlyStatistics = createAsyncThunk(
  'statistics/fetchYearlyStatistics',
  async ({
    __raw,
    year,
    liveData,
    authOrgId,
  }: {
    __raw: string;
    year: number;
    liveData: boolean;
    authOrgId: string;
  }) => {
    try {
      const baseUrl = liveData
        ? `${getCmsApiBaseUrl(true)}/sessions/year?year=${year}`
        : `https://staging.id.sonio-group.com/api/console/v1/sessions/year?year=${year}`;
      const response = await axios.get(`${baseUrl}?authOrgId=${authOrgId}`, {
        headers: {
          Authorization: `Bearer ${__raw}`,
        },
      });

      const data: MonthlyStats[] = response.data.months.map((month: any) => ({
        Date: {
          Year: month.year,
          Month: month.month,
        },
        TotalCount: month.totalCount,
        TotalValidationsPass: month.totalValidationsPass,
        TotalValidationsNeedsReview: month.totalValidationsNeedsReview,
        TotalValidationsFail: month.totalValidationsFail,
      }));

      return data;
    } catch (error) {
      throw error;
    }
  },
);

export const statisticSlice = createSlice({
  name: 'sliceStatistics',
  initialState,
  reducers: {
    setSelectedDateStats: (state, action) => {
      state.selectedDateStats = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchStatistics.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchStatistics.fulfilled,
        (state, action: PayloadAction<{ data: any; period: string }>) => {
          state.isLoading = false;
          state.sliceStatistics = action.payload.data;
          // Try different ways to access the data
          let periodData = null;

          if (action.payload.data[action.payload.period]) {
            periodData = action.payload.data[action.payload.period];
          } else if (action.payload.data.Total !== undefined) {
            periodData = action.payload.data;
          } else if (action.payload.data.stats) {
            periodData = action.payload.data.stats;
          } else if (
            Array.isArray(action.payload.data) &&
            action.payload.data.length > 0
          ) {
            periodData = action.payload.data[0];
          }

          if (periodData) {
            state.selectedDateStats = {
              Total:
                periodData.Total ||
                periodData.total ||
                periodData.totalCount ||
                0,
              Pass:
                periodData.Pass ||
                periodData.pass ||
                periodData.totalValidationsPass ||
                0,
              Fail:
                periodData.Fail ||
                periodData.fail ||
                periodData.totalValidationsFail ||
                0,
              NeedsReview:
                periodData.NeedsReview ||
                periodData.needsReview ||
                periodData.totalValidationsNeedsReview ||
                0,
              Pending: periodData.Pending || periodData.pending || 0,
            };
          } else {
            state.selectedDateStats = {
              Total: 0,
              Pass: 0,
              Fail: 0,
              NeedsReview: 0,
              Pending: 0,
            };
          }

          state.error = null;
        },
      )
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch stats';
        state.sliceStatistics = [];
      })
      .addCase(fetchYearlyStatistics.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchYearlyStatistics.fulfilled,
        (state, action: PayloadAction<MonthlyStats[]>) => {
          state.isLoading = false;
          state.yearlyStatistics = action.payload;
          state.error = null;
        },
      )
      .addCase(fetchYearlyStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch yearly stats';
      });
  },
});

export const { setSelectedDateStats } = statisticSlice.actions;
export default statisticSlice.reducer;
